import * as XLSX from 'xlsx';
import type { Customization } from '../types/customization';
import type { Dish } from '../types/dish';
import type { Recipe } from '../types/recipe';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import {
  applyCukcukRecipeTemplate,
  buildCukcukRecipeExportWorkbook,
  type CukcukRecipeExportSummary,
} from '../utils/cukcukRecipeExport';

export const CUKCUK_RECIPE_EXPORT_CONTENT_TYPE =
  'application/vnd.ms-excel';
export const CUKCUK_RECIPE_TEMPLATE_URL =
  '/templates/cukcuk/DANHMUCTHUCDON-template.xls';

export interface CukcukRecipeExportFileResult extends CukcukRecipeExportSummary {
  blob: Blob;
  filename: string;
}

export interface ExportCukcukRecipesInput {
  dishes: Dish[];
  recipes: Recipe[];
  materials: WarehouseGoodsItem[];
  customizations?: Customization[];
  includeServicePreferences?: boolean;
  includeDraftMaterialMappings?: boolean;
  date?: Date;
  /** Optional template bytes for tests, scripts, or an offline export. */
  templateBytes?: ArrayBuffer | Uint8Array;
  /** Override the public template URL when the caller hosts its own copy. */
  templateUrl?: string;
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const loadTemplateBytes = async (
  input: ExportCukcukRecipesInput,
): Promise<ArrayBuffer | Uint8Array> => {
  if (input.templateBytes) return input.templateBytes;
  const response = await fetch(input.templateUrl || CUKCUK_RECIPE_TEMPLATE_URL);
  if (!response.ok) {
    throw new Error(`Không thể tải mẫu nhập khẩu CUKCUK (HTTP ${response.status}).`);
  }
  return response.arrayBuffer();
};

export const exportCukcukRecipesToFile = async (
  input: ExportCukcukRecipesInput,
): Promise<CukcukRecipeExportFileResult> => {
  const result = buildCukcukRecipeExportWorkbook(input);
  const templateBytes = await loadTemplateBytes(input);
  const workbook = applyCukcukRecipeTemplate(templateBytes, result.workbook);
  // CUKCUK's menu importer is the legacy BIFF8 importer. Although modern
  // Excel opens both formats, it rejects OOXML .xlsx files and also checks
  // that the full downloaded template structure is still present.
  const bytes = XLSX.write(workbook, { bookType: 'biff8', type: 'array' });
  const date = input.date || new Date();

  return {
    ...result.summary,
    blob: new Blob([bytes], { type: CUKCUK_RECIPE_EXPORT_CONTENT_TYPE }),
    filename: `CUKCUK_DinhLuong_8am_${formatDate(date)}${input.includeDraftMaterialMappings ? '_FULL' : ''}.xls`,
  };
};

export const downloadCukcukRecipeExport = (result: CukcukRecipeExportFileResult): void => {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};
